import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { productData } from '../data/mockData';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../lib/analytics';
import { firstStepForPage } from './QuoteCalculator/QuoteProgressSteps';
import { QuoteWizardProgress } from '../components/JourneyStepper';
import { SelectedProductDisplay } from './QuoteCalculator/SelectedProductDisplay';
import { SaveQuoteButton } from '../components/SaveQuoteButton';
import { Step1Category } from './QuoteCalculator/steps/Step1Category';
import { Step2Format } from './QuoteCalculator/steps/Step2Format';
import { Step3Material } from './QuoteCalculator/steps/Step3Material';
import { Step6Shipping } from './QuoteCalculator/steps/Step6Shipping';
import { QuoteHeader } from './QuoteCalculator/QuoteHeader';
import { QuoteFooter } from './QuoteCalculator/QuoteFooter';
import { QuoteWizardNav } from './QuoteCalculator/QuoteWizardNav';
import { WizardPage2Panel } from './QuoteCalculator/WizardPage2Panel';
import {
  exportQuotePdf,
  resolvePricingType,
} from './QuoteCalculator/quotePdfExport';
import {
  dimsInInches as dimsInInchesFn,
  calculateItemsPerPage as calculateItemsPerPageFn,
  sizeMultiplierFromInches,
} from './QuoteCalculator/quoteMath';
import { parseQuoteParams } from './QuoteCalculator/parseQuoteParams';
import {
  FALLBACK_SHIPPING_COSTS_CAD,
  SHIPPING_TIMES,
} from './QuoteCalculator/quoteConstants';
import { useOrderSubmission } from '../hooks/useOrderSubmission';
import { useQuotePricing } from '../hooks/useQuotePricing';
import { isWhiteInkEligible } from '../lib/whiteInkEligibility';
import { useProductSelection } from '../hooks/useProductSelection';
import { useCountryCurrencyAutoSync } from '../hooks/useCountryCurrencyAutoSync';
import { useProductionEstimate } from '../hooks/useProductionEstimate';
import { useDraftQuotePersistence } from '../hooks/useDraftQuotePersistence';
import { usePage2ArtworkState } from '../hooks/usePage2ArtworkState';
import { useShippingAddressState } from '../hooks/useShippingAddressState';
import { useShippingRates } from '../hooks/useShippingRates';
import { buildQuoteDraftUrl, getQuoteProductName } from '../lib/quoteDraftUrl';
import { QuoteModalsContainer } from './QuoteCalculator/QuoteModalsContainer';
import { log } from '../lib/logger';

const QuoteCalculator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, isEvergreenTier, refreshAuth } = useAuth();

  // GA4 conversion funnel — one quote_started per wizard mount.
  useEffect(() => {
    trackEvent('quote_started', { page_path: window.location.pathname });
  }, []);

  // Parse URL parameters for pre-selection (see QuoteCalculator/parseQuoteParams)
  const preset = parseQuoteParams(location.search);
  const preselectedProduct = preset.product;
  const preselectedFormat = preset.format;
  const preselectedMaterial = preset.material;
  const preselectedWidth = preset.width;
  const preselectedHeight = preset.height;
  const preselectedUnit = preset.unit;
  const preselectedQuantityParam = preset.quantity;
  const preselectedShipping = preset.shipping;
  const initialStep = preset.step;

  const [step, setStep] = useState(initialStep);
  // Auto scroll-to-top whenever the wizard advances/recedes. The
  // global `<ScrollToTop />` mounted at the router root only fires on
  // `pathname` changes — the QuoteCalculator advances its internal
  // step state without changing the URL, so Continue from the bottom
  // of PAGE 2 would otherwise leave the user looking at the bottom
  // of PAGE 3. (iter114s.59 — operator brief: "ensure the next page
  // automatically loads/scrolls to the top of the viewport so the
  // user never has to manually scroll upward after navigation.")
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [step]);
  // Guest-checkout prompt: shown when an anonymous visitor clicks "Place
  // Order". When they finish the short form, handlePlaceOrder re-runs.
  const [guestPromptOpen, setGuestPromptOpen] = useState(false);

  // Page 2 artwork collector — MANDATORY upload + cutline + instructions.
  // File is POSTed to /api/files/artwork/staging-upload the moment it's
  // dropped; the returned staging artwork_id is adopted into the order on
  // Place Order via POST /artwork/staging/{id}/attach. Hook bundles the
  // four state slices + auto-derives `selectedShape` from the cutline
  // picker (custom → die-cut; rectangle/rounded/circle → standard) so
  // pricing/submission/PDF export all see one source of truth.
  const {
    page2ArtworkFile,
    setPage2ArtworkFile,
    page2ArtworkId,
    setPage2ArtworkId,
    page2CutlineShape,
    setPage2CutlineShape,
    page2ArtworkInstructions,
    setPage2ArtworkInstructions,
    selectedShape,
    setSelectedShape,
  } = usePage2ArtworkState();
  const [selectedProduct, setSelectedProduct] = useState(preselectedProduct);
  const [selectedFormat, setSelectedFormat] = useState(preselectedFormat);
  const [selectedMaterial, setSelectedMaterial] = useState(preselectedMaterial);

  // Auto-derive `selectedShape` from the Page-2 CUT SHAPE picker so we
  // don't need a duplicate Die-Cut/Standard radio group on Page 2.
  // (Logic now lives in usePage2ArtworkState — kept comment for context.)
  // White-ink toggle for transparent / clear / specialty products. Only
  // surfaced when isWhiteInkEligible(format, material) returns true.
  // iter114s.9 — auto-default to 'clear' when the customer lands on a
  // white-ink-eligible material (matches the customer's expected
  // behaviour: holographic / glitter / clear / foil products almost
  // always want the design's negative space to show the special
  // backing). The `whiteInkUserPickedRef` tracks whether the customer
  // has explicitly toggled the option themselves — once they have,
  // the auto-default never reverts their pick on subsequent
  // material/format changes.
  const [whiteInkOption, setWhiteInkOption] = useState('keep');
  const whiteInkUserPickedRef = useRef(false);
  const handleSetWhiteInkOption = useCallback((value) => {
    whiteInkUserPickedRef.current = true;
    setWhiteInkOption(value);
  }, []);
  const [width, setWidth] = useState(preselectedWidth);
  const [height, setHeight] = useState(preselectedHeight);
  const [unit, setUnit] = useState(preselectedUnit);
  const [quantity, setQuantity] = useState(preselectedQuantityParam);
  // iter114s.63 — When the customer reaches Step 4 (Size & Quantity)
  // without a quantity already chosen via URL params, default to 100
  // so the Continue button isn't disabled out of the gate. The
  // previous "must pick a qty before advancing" UX read as an artwork
  // upload gate to first-time visitors (the artwork dropzone is the
  // visual focus of Page 2; a disabled Continue button after typing
  // dimensions felt like "I haven't uploaded yet"). The customer is
  // free to change the default from the Step 4 controls.
  useEffect(() => {
    if (step === 4 && (quantity === null || quantity === undefined || !Number.isFinite(quantity))) {
      setQuantity(100);
    }
    // Only run when entering step 4 — re-running on every quantity
    // change would resurrect 100 if the customer tries to clear the
    // field while editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const [customQuantity, setCustomQuantity] = useState('');
  const [shippingMethod, setShippingMethod] = useState(preselectedShipping);
  // Shipping/country/currency cluster — bundled into a hook so the
  // page body stays focused on flow. Includes the userSet flags for
  // currency + country so manual picks aren't clobbered by auto-sync.
  const {
    shippingAddress,
    setShippingAddress,
    country,
    setCountry,
    countryUserSet,
    setCountryUserSet,
    currency,
    setCurrency,
    currencyUserSet,
    setCurrencyUserSet,
    isShippingAddressComplete,
  } = useShippingAddressState(user);
  const [imageEnlarged, setImageEnlarged] = useState(false);
  const [enlargedImageSrc, setEnlargedImageSrc] = useState('');
  // Edit-flow: when user came back here from UploadArtwork's "Go Back & Resize",
  // we have an existing orderId with artwork already uploaded. Hook owns the
  // backend production-size estimate fetch + caching.
  const editingOrderId = location.state?.orderId;
  const productionEstimate = useProductionEstimate({
    orderId: editingOrderId,
    width,
    height,
    unit,
  });

  // Shipping address fields for Step 6 — owned by useShippingAddressState above.
  // (companyName, contactName, addressLine1, addressLine2, city,
  //  stateProvince, postalCode)

  // Debug: Log authentication state changes (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      log.info('Auth state:', {
        loading,
        isAuthenticated,
        hasUser: !!user,
        userName: user?.name
      });
    }
  }, [loading, isAuthenticated, user]);

  // iter114s.9 — when the customer lands on a white-ink-eligible
  // product/material combo, default the white-ink option to 'clear'
  // so the live mockup immediately shows the holographic / glitter /
  // foil texture through the design's negative space (the customer-
  // flagged "auto-pick Make white areas clear on eligible products"
  // ask). We respect any manual pick the customer has already made
  // via `whiteInkUserPickedRef` — once the customer toggles the
  // option themselves the auto-default never reverts their choice.
  useEffect(() => {
    if (whiteInkUserPickedRef.current) return;
    if (isWhiteInkEligible(selectedFormat, selectedMaterial)) {
      setWhiteInkOption((prev) => (prev === 'clear' ? prev : 'clear'));
    }
  }, [selectedFormat, selectedMaterial]);

  // Auto-sync Country + Currency from the user's saved profile (handles
  // the AuthContext-hydrate-after-mount race). Manual dropdown changes
  // are preserved via the *UserSet flags. Extracted into a hook so the
  // page body stays focused on flow.
  useCountryCurrencyAutoSync({
    user,
    countryUserSet,
    currencyUserSet,
    setCountry,
    setCurrency,
  });

  // When user returns here via "Go Back & Resize" from UploadArtwork (an
  // editSize flow on an existing order), the production-size estimate is
  // fetched by useProductionEstimate above. Nothing to do here.

  // iter114s.17 — Shipping methods are tier-aware on country:
  //   * Canada → single "Standard Shipping (1-2 days)" — domestic
  //     courier covers the whole country in 1-2 business days, so the
  //     customer doesn't need to pick between 3 tiers. Customer ask
  //     2026-05-08.
  //   * USA / other → keep the original 3 tiers (Standard / Rush /
  //     Express) so international orders can still upgrade for speed.
  //
  // BUG FIX (iter114s.17): the FALLBACK_SHIPPING_COSTS_CAD constants
  // were stored as CAD numbers but `formatCurrency` already multiplies
  // by `currencyRates[CAD]` (= 1.37) to convert the wizard's USD-equiv
  // base into CAD for display — same convention as the rest of
  // `useQuotePricing`. Without dividing the fallback by 1.37 here, a
  // Canadian customer was seeing CAD $4.99 × 1.37 = $6.84 (etc.), which
  // is exactly what the customer flagged as "doesn't appear to be
  // pulling true live freightcom pricing". Numbers now match the
  // raw CAD constants once formatCurrency converts back.
  const shippingCosts = country === 'Canada'
    ? { standard: FALLBACK_SHIPPING_COSTS_CAD.standard / 1.37 }
    : {
        standard: FALLBACK_SHIPPING_COSTS_CAD.standard / 1.37,
        rush: FALLBACK_SHIPPING_COSTS_CAD.rush / 1.37,
        express: FALLBACK_SHIPPING_COSTS_CAD.express / 1.37,
      };

  const shippingTimes = country === 'Canada'
    ? { standard: '1 - 2 Days' }
    : SHIPPING_TIMES;

  // iter114s.18 — Live Freightcom rates inside the Quote Calculator's
  // Step 6. Until now the in-wizard step rendered static fallback
  // prices and only the post-quote `CustomerInfoPage` / `ReviewOrderPage`
  // hit `/api/shipping/rates`. The customer asked for live carrier
  // pricing right here in the wizard so the quote total matches what
  // they'd actually be charged, not a placeholder.
  //
  // Strategy: when the customer's shipping address is complete and we
  // know what they're shipping (product/format/quantity), debounce a
  // fetch of the top-N cheapest live carriers. We pass top_n=1 for
  // Canada (customer asked for a single "Standard 1-2 Days" tile) and
  // top_n=3 elsewhere. Until the live rates land, the existing static
  // fallback above keeps the wizard usable.
  const liveShipping = useShippingRates();
  const [selectedLiveRate, setSelectedLiveRate] = useState(null);

  useEffect(() => {
    if (!isShippingAddressComplete()) return undefined;
    if (!selectedProduct || !selectedFormat || !quantity) return undefined;
    const province = shippingAddress.stateProvince;
    if (!province || !shippingAddress.postalCode) return undefined;

    const countryISO = country?.toLowerCase().includes('canad')
      ? 'CA'
      : country?.toLowerCase().includes('states')
        ? 'US'
        : country?.toLowerCase().includes('mexico')
          ? 'MX'
          : country?.toLowerCase().includes('kingdom')
            ? 'GB'
            : 'CA';

    const handle = setTimeout(() => {
      liveShipping
        .fetchRates({
          shipTo: {
            street: shippingAddress.addressLine1 || 'Address TBD',
            city: shippingAddress.city,
            province,
            postal_code: shippingAddress.postalCode,
            country: countryISO,
            residential: true,
          },
          items: [
            {
              category: selectedProduct,
              format: selectedFormat,
              subcategory: selectedFormat,
              quantity: parseInt(quantity, 10) || 1,
            },
          ],
        })
        .catch(() => {
          /* error already captured by the hook */
        });
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shippingAddress.addressLine1,
    shippingAddress.city,
    shippingAddress.stateProvince,
    shippingAddress.postalCode,
    country,
    selectedProduct,
    selectedFormat,
    quantity,
  ]);

  // Trim live rates per country: Canada → 1, others → up to 3.
  const trimmedLiveRates = country === 'Canada'
    ? liveShipping.rates.slice(0, 1)
    : liveShipping.rates.slice(0, 3);

  // Auto-pick cheapest live rate as the customer's selection so the
  // quote total reflects it immediately.
  useEffect(() => {
    if (!trimmedLiveRates.length) return;
    if (selectedLiveRate && trimmedLiveRates.find((r) => r.service_code === selectedLiveRate.service_code)) {
      return;
    }
    setSelectedLiveRate(trimmedLiveRates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedLiveRates.length, country]);

  // iter114s.17 — when the country flips to Canada, force the
  // shippingMethod back to 'standard' so we don't end up with the
  // selected method ('rush' / 'express') no longer existing in the
  // collapsed Canadian shippingCosts map (which would zero-out the
  // shipping line and crash `formatCurrency(undefined)`).
  useEffect(() => {
    if (country === 'Canada' && shippingMethod !== 'standard') {
      setShippingMethod('standard');
    }
  }, [country, shippingMethod]);

  // iter112: human-friendly labels for the Quick Quote configuration
  // summary on Step 6. We titleise the kebab-case IDs from the wizard
  // state so the customer sees "Custom Stickers · Matte · Standard
  // 6-7 Days" instead of "custom-stickers · matte · standard". Pure
  // formatting — no business state lives here.
  const titleise = (s) =>
    typeof s === 'string'
      ? s.replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
      : '';
  const formatLabel = titleise(selectedFormat);
  const materialLabel = titleise(selectedMaterial);
  // iter114s.18 — when a live Freightcom rate is selected it drives
  // the configuration-summary label too, so the quote card reflects
  // the actual carrier + service the customer picked instead of the
  // static "Standard · 6-7 Days" placeholder.
  const shippingMethodLabel = selectedLiveRate
    ? `${selectedLiveRate.carrier_name} · ${selectedLiveRate.service_level}${
        selectedLiveRate.estimated_delivery_days != null
          ? ` · ${selectedLiveRate.estimated_delivery_days} day${selectedLiveRate.estimated_delivery_days === 1 ? '' : 's'}`
          : ''
      }`
    : shippingMethod && shippingTimes[shippingMethod]
      ? `${titleise(shippingMethod)} · ${shippingTimes[shippingMethod]}`
      : titleise(shippingMethod);

  // iter114s.59 — Track the customer's ORIGINAL size when they reach
  // the "Available Across Multiple Configurations" swap UI on Step 6.
  // Per operator brief: "if the user wants to revert back to their
  // previously configured option/state without resetting the entire
  // flow", the original pill must stay visible alongside the live
  // alternates so any click — original or alternate — swaps freely.
  // Resets whenever the customer comes back to Page 2 and edits W/H
  // manually, since the new manual value IS the new original.
  const [originalSize, setOriginalSize] = useState(null);

  // Click handler for the "Available Across Multiple Configurations"
  // size tiles on Step 6 — swap the wizard's size to the alternate
  // and force unit to inches so the displayed labels match the
  // resulting form state. Quantity stays the same.
  const handlePickAlternateSize = (wIn, hIn) => {
    // First click captures the current configuration as "Original"
    // so the customer can always swap back from any future picks.
    setOriginalSize((prev) => {
      if (prev) return prev;
      const currentWIn = unit === 'centimeters' ? parseFloat(width) / 2.54 : parseFloat(width);
      const currentHIn = unit === 'centimeters' ? parseFloat(height) / 2.54 : parseFloat(height);
      if (!Number.isFinite(currentWIn) || !Number.isFinite(currentHIn)) return prev;
      return { wIn: currentWIn, hIn: currentHIn };
    });
    setUnit('inches');
    setWidth(String(wIn));
    setHeight(String(hIn));
  };

  // Resets `originalSize` whenever the customer rewinds to Page 2 and
  // changes dimensions manually. The new manual value becomes the new
  // "original" baseline for any future alternate swaps.
  useEffect(() => {
    if (step <= 5) setOriginalSize(null);
    // Intentionally only re-runs when `step` crosses the Page 2 boundary;
    // we don't want manual fiddling on Step 6 itself to wipe the baseline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Image enlargement handlers
  const handleImageClick = (imageSrc) => {
    setEnlargedImageSrc(imageSrc);
    setImageEnlarged(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const handleCloseEnlargedImage = () => {
    setImageEnlarged(false);
    setEnlargedImageSrc('');
    document.body.style.overflow = 'unset'; // Re-enable scrolling
  };

  // Pure pricing-math helpers live in QuoteCalculator/quoteMath.js —
  // these wrappers close over the current unit so call sites stay terse.
  const dimsInInches = () => dimsInInchesFn(width, height, unit);
  const calculateItemsPerPage = (w, h) => calculateItemsPerPageFn(w, h, unit);
  const sizeMultiplier = () => {
    const { wIn, hIn } = dimsInInches();
    return sizeMultiplierFromInches(wIn, hIn);
  };

  // Product-catalogue derivations now live in useProductSelection — keeps
  // QuoteCalculator focused on flow + UI state instead of re-traversing
  // mockData.categories on every helper.
  const {
    getAvailableMaterials,
    getQuantityConfig,
    requiresCustomQuote,
    requiresCustomQuoteAtQuantity,
    checkSizeLimit: checkSizeLimitFn,
  } = useProductSelection({ selectedProduct, selectedFormat, selectedMaterial });

  // Bind the helper so existing call-sites (`checkSizeLimit()` with the
  // current width/height) keep working without prop drilling.
  const checkSizeLimit = () => checkSizeLimitFn(width, height);


  // Auto-skip Step 2 if the selected category has only ONE format
  // (iter114s.11 — the customer's "skip 1-card pickers" UX win). When
  // it skips, Step 3 (material) may also auto-skip via the existing
  // single-material effect below — net result for a pure single-
  // option category is Page 1 → Page 4 (size) directly.
  useEffect(() => {
    if (step !== 2 || !selectedProduct) return;
    const cat = productData.categories?.find((c) => c.id === selectedProduct);
    const subs = cat?.subcategories || [];
    if (subs.length === 1) {
      setSelectedFormat(subs[0].id);
      setStep(3);
    }
  }, [step, selectedProduct]);

  // Auto-skip Step 3 if only one material available
  useEffect(() => {
    if (step === 3 && selectedProduct && selectedFormat && !requiresCustomQuote()) {
      const availableMaterials = getAvailableMaterials();

      if (availableMaterials && availableMaterials.length === 1) {
        setSelectedMaterial(availableMaterials[0].id);
        setStep(4); // Skip to size
      }
    }
  }, [step, selectedProduct, selectedFormat, requiresCustomQuote, getAvailableMaterials]);

  // Generate PDF Quote
  const generatePDF = () => exportQuotePdf({
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    selectedShape,
    width,
    height,
    unit,
    quantity,
    shippingMethod,
    shippingTimes,
    country,
    availableMaterials: getAvailableMaterials(),
    subtotal,
    shipping,
    total,
    formatCurrency,
  });

  // Custom Quote modal — replaces the legacy mailto flow. We open the
  // in-app form prefilled with whatever the user has already selected
  // in the quote wizard, then show a 5-second success toast on submit.
  const [customQuoteOpen, setCustomQuoteOpen] = useState(false);
  const [quoteSentToastOpen, setQuoteSentToastOpen] = useState(false);
  const handleCustomQuoteRequest = () => setCustomQuoteOpen(true);
  const customQuotePrefill = useMemo(() => {
    const cat = productData.categories.find((c) => c.id === selectedProduct);
    const sub = cat?.subcategories?.find((s) => s.id === selectedFormat);
    return {
      category: cat?.name || '',
      productType: sub?.name || '',
      product: [cat?.name, sub?.name].filter(Boolean).join(' — '),
    };
  }, [selectedProduct, selectedFormat]);

  const pricingType = resolvePricingType(selectedProduct, selectedFormat);
  const isIndividualPricing = pricingType === 'individual';

  // Pricing hook — owns the backend prefetch + primary/fallback math.
  // Replaces the ~130 LOC calculatePrice useCallback that used to live
  // inline here. See hooks/useQuotePricing.js for the full logic.
  const {
    calculatePrice,
    subtotal,
    backendCombo,
    backendPriceExtrapolated,
    backendRequiresCustomQuote,
  } = useQuotePricing({
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    width,
    height,
    unit,
    quantity,
    isEvergreenTier,
    isIndividualPricing,
  });

  const formatCurrency = (amount) => {
    const rate = productData.currencyRates[currency];
    const convertedAmount = amount * rate;
    return `${currency} $${convertedAmount.toFixed(2)}`;
  };

  // iter114s.18 — `shipping` is in USD-equivalent so `formatCurrency`
  // multiplies by `currencyRates[CAD]` for CAD display. Live Freightcom
  // returns CAD totals, so divide by 1.37 to match the wizard
  // convention. When a live rate is selected it always wins over the
  // static fallback; otherwise we fall back to the static value.
  const shipping = selectedLiveRate
    ? selectedLiveRate.total_price / 1.37
    : shippingCosts[shippingMethod] ?? 0;
  const total = subtotal ? (subtotal + shipping).toFixed(2) : '0.00';

  // Place-Order flow: create draft order → add item → attach staged Page 2
  // artwork → navigate to /review-order (or /upload-artwork if no staged
  // artwork). Extracted into a reusable hook; also owns the post-guest-login
  // retry effect.
  const { handlePlaceOrder, setPendingPlaceOrder, submitting: placingOrder } = useOrderSubmission({
    location,
    navigate,
    user,
    isAuthenticated,
    loading,
    isShippingAddressComplete,
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    selectedShape,
    width,
    height,
    unit,
    quantity,
    shippingMethod,
    country,
    shippingAddress,
    subtotal,
    // Only thread the white-ink option when the eligibility predicate
    // matched — otherwise omit so non-eligible items stay clean.
    whiteInkOption: isWhiteInkEligible(selectedFormat, selectedMaterial)
      ? whiteInkOption
      : undefined,
    page2ArtworkFile,
    page2ArtworkId,
    page2CutlineShape,
    page2ArtworkInstructions,
    getAvailableMaterials,
    setGuestPromptOpen,
  });

  // -------------------------------------------------------------------- //
  // Draft persistence: mirror current calculator state to sessionStorage  //
  // every render so the <TopNav> and <FloatingQuoteSummary> can display   //
  // "Resume draft" + live totals across every page of the site.           //
  // Hook handles the productData lookup + URL composition + try/catch.    //
  // -------------------------------------------------------------------- //
  useDraftQuotePersistence({
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    width,
    height,
    unit,
    quantity,
    shippingMethod,
    total,
    currency,
  });

  const canProceed = () => {
    if (step === 1) return selectedProduct !== '';
    if (step === 2) return selectedFormat !== '';
    if (step === 3) return selectedMaterial !== '';
    // Combined Wizard Page 2 (Size + Quantity + Artwork) — require ALL THREE
    // to be valid before the user can advance to the Shipping page.
    //
    // iter114s.68 — Artwork is now a hard gate: the Continue button stays
    // disabled until the staging upload completes (and we hold a
    // `page2ArtworkId`). Without this gate first-time visitors could click
    // Continue while their artwork was still uploading (or never started
    // an upload at all), then hit a confusing 422 on Place Order. Holding
    // them at Page 2 until the upload finishes keeps the failure mode
    // visible at the point of correction.
    if (step === 4 || step === 5) {
      if (!width || !height) return false;
      const sizeCheck = checkSizeLimit();
      if (!sizeCheck.valid) return false;
      const qtyValid =
        quantity !== null && quantity !== undefined && !isNaN(quantity) && quantity > 0;
      if (!qtyValid) return false;
      // Artwork upload gate — the staging artwork_id is populated only
      // after the dropzone's POST to /artwork/staging-upload returns 2xx.
      if (!page2ArtworkId) return false;
      return true;
    }
    return true;
  };

  // SaveQuoteButton props are used from 2 places (the global render for
  // steps 3 & 6, and the Page-2 inline render). Pull from the shared
  // lib/quoteDraftUrl helpers so both call sites compose the same URL +
  // product name without drift.
  const saveQuoteDraftUrl = buildQuoteDraftUrl({
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    width,
    height,
    unit,
    quantity,
    shippingMethod,
    step,
  });
  const saveQuoteProductName = getQuoteProductName(selectedProduct, selectedFormat);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <QuoteHeader
        currency={currency}
        onCurrencyChange={(v) => { setCurrency(v); setCurrencyUserSet(true); }}
      />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-[#4A7C59] mb-2">Quote Calculator & Order Form</h2>
          <p className="text-gray-600 text-lg">Need an instant quote? Select your order details below to see your completed pricing with shipping included.</p>
          {/* iter114s.17 — pricing-tier badge ("StickerYou Pricing (CAD)"
              / "Discount reflected in price") removed at customer
              request. The pricing tier still drives the catalog math
              under the hood; we just no longer surface a label here. */}
        </div>

        {/* iter114s.59 — Replaced the static `<JourneyStepper
            currentStage={1} />` with a wizard-internal 3-page
            tracker that actually reflects the customer's progress
            through the QuoteCalculator's three pages (Product /
            Size & Quantity / Shipping). The previous chrome left
            "Configure Product" stuck-green even on PAGE 3 · Shipping,
            which read as a bug. The outer 5-stage JourneyStepper
            re-appears once the customer leaves /quote for
            /upload-artwork etc. */}
        <QuoteWizardProgress
          step={step}
          onJump={(pageN) => {
            // Map page → wizard step: page 1 → step 3 (last sub-step
            // of Product page, so saved selections still render),
            // page 2 → step 4, page 3 → step 6.
            if (pageN === 1) setStep(Math.min(3, step));
            else if (pageN === 2) setStep(4);
            else if (pageN === 3) setStep(6);
          }}
        />

        {/* Step Content */}
        <Card className="shadow-lg border-2 border-green-100">
          <CardHeader className="bg-gradient-to-r from-[#4A7C59] to-[#6FBF73] text-white">
            {/* iter114s.69 — Page 2 header restructured per operator brief:
                title + description block stays on the LEFT (at the
                previous compact size — `text-xl`, not the bloated
                `text-2xl` that snuck in), Selected Product card moves
                to the RIGHT of that text instead of below it. On steps
                1-3 (PAGE 1) and step 6 (PAGE 3) the row collapses to a
                single column because there is no header SelectedProduct
                affordance (step ≥ 4 only). */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-xl md:text-2xl" data-testid="wizard-page-title">
                  {(step === 1 || step === 2 || step === 3) && 'PAGE 1 · Product'}
                  {(step === 4 || step === 5) && 'PAGE 2 · Size & Quantity'}
                  {step === 6 && 'PAGE 3 · Shipping'}
                </CardTitle>
                <CardDescription className="text-green-50">
                  {step === 1 && 'Choose a Product'}
                  {step === 2 && 'Choose a Format'}
                  {step === 3 && 'Choose a Material'}
                  {(step === 4 || step === 5) && 'Pick your dimensions and quantity — the price updates live below'}
                  {step === 6 && 'Shipping & Final Quote'}
                </CardDescription>
              </div>
              {step >= 3 && (
                <div className="md:flex-shrink-0 md:max-w-[55%]">
                  <SelectedProductDisplay
                    variant="header"
                    selectedProduct={selectedProduct}
                    selectedFormat={selectedFormat}
                    selectedMaterial={selectedMaterial}
                    step={step}
                    materials={getAvailableMaterials()}
                    onImageClick={handleImageClick}
                  />
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* iter114s.73 — In-body Selected Product display on Step 3
                has been REMOVED. It now lives in the green CardHeader
                alongside the title (universal for steps 3-6), matching
                Page 2 / Page 3 chrome so the customer's selected
                product reads as part of the page-level affordance
                rather than a separate full-width preview panel
                stacked above the material picker. */}

            {/* Save / share -- appears once user has enough state to be worth
                saving. On Page 2 (steps 4/5) we render it INSIDE the flow
                (directly under Select Quantity) per the redesign, so the
                global render is suppressed there. */}
            {selectedProduct && selectedFormat && step >= 3 && step !== 4 && step !== 5 && (
              <SaveQuoteButton
                draftUrl={saveQuoteDraftUrl}
                productName={saveQuoteProductName}
                totalPrice={total ? parseFloat(total) : 0}
                currency={currency}
              />
            )}

            {/* Step 1: Product Selection */}
            {step === 1 && (
              <Step1Category
                productData={productData}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                setStep={setStep}
              />
            )}

            {/* Step 2: Format/Subcategory Selection */}
            {step === 2 && (
              <Step2Format
                productData={productData}
                selectedProduct={selectedProduct}
                selectedFormat={selectedFormat}
                setSelectedFormat={setSelectedFormat}
                setStep={setStep}
              />
            )}

            {/* Step 3: Material Selection */}
            {step === 3 && (
              <Step3Material
                requiresCustomQuote={requiresCustomQuote}
                handleCustomQuoteRequest={handleCustomQuoteRequest}
                getAvailableMaterials={getAvailableMaterials}
                selectedMaterial={selectedMaterial}
                setSelectedMaterial={setSelectedMaterial}
                onSelect={() => setStep(4)}
              />
            )}

            {/* Wizard Page 2 — Size + Quantity shown TOGETHER. Delegates to
             * WizardPage2Panel so the render block here stays a thin
             * composition. We keep the internal 6-step state machine
             * (validators, foil price hooks, page-maker detection,
             * special-quote gates all key off `step`) but render both
             * Step4 and Step5 when the user is on either.
             */}
            {(step === 4 || step === 5) && (
              <WizardPage2Panel
                selectedProduct={selectedProduct}
                selectedFormat={selectedFormat}
                selectedMaterial={selectedMaterial}
                getAvailableMaterials={getAvailableMaterials}
                backendCombo={backendCombo}
                selectedShape={selectedShape}
                setSelectedShape={setSelectedShape}
                width={width}
                setWidth={setWidth}
                height={height}
                setHeight={setHeight}
                unit={unit}
                setUnit={setUnit}
                quantity={quantity}
                setQuantity={setQuantity}
                customQuantity={customQuantity}
                setCustomQuantity={setCustomQuantity}
                page2ArtworkFile={page2ArtworkFile}
                setPage2ArtworkFile={setPage2ArtworkFile}
                page2ArtworkId={page2ArtworkId}
                setPage2ArtworkId={setPage2ArtworkId}
                page2CutlineShape={page2CutlineShape}
                setPage2CutlineShape={setPage2CutlineShape}
                page2ArtworkInstructions={page2ArtworkInstructions}
                setPage2ArtworkInstructions={setPage2ArtworkInstructions}
                whiteInkOption={whiteInkOption}
                setWhiteInkOption={handleSetWhiteInkOption}
                subtotal={subtotal}
                total={total}
                backendPriceExtrapolated={backendPriceExtrapolated}
                backendRequiresCustomQuote={backendRequiresCustomQuote}
                checkSizeLimit={checkSizeLimit}
                handleCustomQuoteRequest={handleCustomQuoteRequest}
                requiresCustomQuoteAtQuantity={requiresCustomQuoteAtQuantity}
                getQuantityConfig={getQuantityConfig}
                calculateItemsPerPage={calculateItemsPerPage}
                calculatePrice={calculatePrice}
                formatCurrency={formatCurrency}
                isAuthenticated={isAuthenticated}
                onRequireAuth={() => setGuestPromptOpen(true)}
                refreshAuth={refreshAuth}
                saveQuoteDraftUrl={saveQuoteDraftUrl}
                saveQuoteProductName={saveQuoteProductName}
                currency={currency}
              />
            )}

            {/* Step 6: Shipping & Quote */}
            {step === 6 && (
              <Step6Shipping
                shippingAddress={shippingAddress}
                setShippingAddress={setShippingAddress}
                country={country}
                setCountry={setCountry}
                countryUserSet={countryUserSet}
                setCountryUserSet={setCountryUserSet}
                shippingMethod={shippingMethod}
                setShippingMethod={setShippingMethod}
                shippingCosts={shippingCosts}
                shippingTimes={shippingTimes}
                isShippingAddressComplete={isShippingAddressComplete}
                subtotal={subtotal}
                shipping={shipping}
                total={total}
                quantity={quantity}
                isIndividualPricing={isIndividualPricing}
                calculateItemsPerPage={calculateItemsPerPage}
                width={width}
                height={height}
                unit={unit}
                sizeMultiplier={sizeMultiplier}
                dimsInInches={dimsInInches}
                calculatePrice={calculatePrice}
                productionEstimate={productionEstimate}
                generatePDF={generatePDF}
                handlePlaceOrder={handlePlaceOrder}
                loading={loading}
                placingOrder={placingOrder}
                formatCurrency={formatCurrency}
                selectedProduct={titleise(selectedProduct)}
                selectedFormat={selectedFormat}
                selectedMaterial={selectedMaterial}
                onPickAlternateSize={handlePickAlternateSize}
                originalSize={originalSize}
                formatLabel={formatLabel}
                materialLabel={materialLabel}
                shippingMethodLabel={shippingMethodLabel}
                backendCombo={backendCombo}
                isEvergreenTier={isEvergreenTier}
                liveRates={trimmedLiveRates}
                liveRatesLoading={liveShipping.loading}
                liveRatesError={liveShipping.error}
                liveRatesMockMode={liveShipping.mockMode}
                liveRatesPackage={liveShipping.package}
                liveRatesTotalCarriers={liveShipping.totalCarriers}
                selectedLiveRate={selectedLiveRate}
                setSelectedLiveRate={setSelectedLiveRate}
              />
            )}

            {/* Navigation Buttons — extracted into QuoteWizardNav */}
            <QuoteWizardNav
              step={step}
              setStep={setStep}
              canProceed={canProceed}
              requiresCustomQuote={requiresCustomQuote}
              getAvailableMaterials={getAvailableMaterials}
              singleFormatCategory={(() => {
                const cat = productData.categories?.find((c) => c.id === selectedProduct);
                return (cat?.subcategories || []).length === 1;
              })()}
              page2ArtworkId={page2ArtworkId}
            />
          </CardContent>
        </Card>
      </div>

      <QuoteFooter />

      <QuoteModalsContainer
        imageEnlarged={imageEnlarged}
        enlargedImageSrc={enlargedImageSrc}
        handleCloseEnlargedImage={handleCloseEnlargedImage}
        guestPromptOpen={guestPromptOpen}
        setGuestPromptOpen={setGuestPromptOpen}
        setPendingPlaceOrder={setPendingPlaceOrder}
        customQuoteOpen={customQuoteOpen}
        setCustomQuoteOpen={setCustomQuoteOpen}
        customQuotePrefill={customQuotePrefill}
        quoteSentToastOpen={quoteSentToastOpen}
        setQuoteSentToastOpen={setQuoteSentToastOpen}
        placingOrder={placingOrder}
      />
    </div>
  );
};

export default QuoteCalculator;
